import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ConnectionType } from '@/lib/api/types.gen';
import { useCreateBridge, useEdgeLinkBridge, useUpdateBridge } from '@/lib/api/edgelink';
import { EdgeLinkBridgeDetail } from '@/lib/api/edgelink/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { CidrInput, validateCidr } from '@/components/ui/cidr-input';
import { cn } from '@/lib/utils';
import { Cloud, Shield, Lock, Loader2, Network } from 'lucide-react';

type BridgeType = 'cloudflared' | 'ipsec';

const createBridgeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  initialRouteCidr: z
    .string()
    .optional()
    .refine((val) => !val || validateCidr(val).isValid, (val) => ({
      message: validateCidr(val ?? '').errorMessage || 'Invalid CIDR',
    })),
});

type CreateBridgeFormValues = z.infer<typeof createBridgeSchema>;

interface CreateBridgeStepProps {
  onBridgeCreated: (bridge: EdgeLinkBridgeDetail) => void;
  onCancel: () => void;
  /** If provided, component enters edit mode instead of create mode */
  existingBridge?: EdgeLinkBridgeDetail | null;
}

const bridgeTypes = [
  {
    id: 'cloudflared' as const,
    label: 'Cloudflare Tunnel',
    icon: Cloud,
    description: "Fast, secure tunnel via Cloudflare's edge network",
    available: true,
  },
  {
    id: 'ipsec' as const,
    label: 'IPSec VPN',
    icon: Shield,
    description: 'Traditional site-to-site VPN connection',
    available: false,
    disabledReason: 'Coming Soon',
  },
];

export default function CreateBridgeStep({
  onBridgeCreated,
  onCancel,
  existingBridge,
}: CreateBridgeStepProps) {
  const isEditMode = Boolean(existingBridge);

  // Derive type from existing bridge in edit mode
  const existingType: BridgeType =
    existingBridge?.summary.type === ConnectionType.CloudflareIpsec ? 'ipsec' : 'cloudflared';

  const [selectedType, setSelectedType] = useState<BridgeType>(
    isEditMode ? existingType : 'cloudflared'
  );
  const [createdBridgeId, setCreatedBridgeId] = useState<string | null>(null);

  const createBridgeMutation = useCreateBridge();
  const updateBridgeMutation = useUpdateBridge();

  // Fetch full bridge detail (with authToken) after creation (only in create mode)
  const { data: bridgeDetail } = useEdgeLinkBridge(createdBridgeId || '', !!createdBridgeId);

  // When bridge detail is loaded after creation, pass it to parent
  React.useEffect(() => {
    if (bridgeDetail && createdBridgeId) {
      onBridgeCreated(bridgeDetail);
    }
  }, [bridgeDetail, createdBridgeId, onBridgeCreated]);

  const form = useForm<CreateBridgeFormValues>({
    resolver: zodResolver(createBridgeSchema),
    defaultValues: {
      name: existingBridge?.summary.name || '',
      initialRouteCidr: existingBridge?.summary.routes?.[0]?.edgeAddressRangeCidr || '0.0.0.0/0',
    },
  });

  // Auto-focus the name field when step mounts
  React.useEffect(() => {
    const timer = setTimeout(() => {
      form.setFocus('name');
    }, 0);
    return () => clearTimeout(timer);
  }, [form]);

  const onSubmit = async (data: CreateBridgeFormValues) => {
    if (isEditMode && existingBridge) {
      // Edit mode: update the existing bridge
      // Note: userConfig must be empty string (not omitted) due to backend proto serialization bug
      const updatedBridge = await updateBridgeMutation.mutateAsync({
        id: existingBridge.summary.id,
        data: { name: data.name, userConfig: '' },
      });
      // Pass the updated bridge back (it already has authToken)
      onBridgeCreated({
        ...existingBridge,
        summary: updatedBridge.summary,
      });
    } else {
      // Create mode: create a new bridge
      const connectionType =
        selectedType === 'cloudflared' ? ConnectionType.Cloudflared : ConnectionType.CloudflareIpsec;

      const bridge = await createBridgeMutation.mutateAsync({
        name: data.name,
        type: connectionType,
        initialRouteCidr: data.initialRouteCidr || undefined,
      });

      // Set the ID to trigger fetching full detail with authToken
      setCreatedBridgeId(bridge.id);
    }
  };

  const isPending =
    createBridgeMutation.isPending ||
    updateBridgeMutation.isPending ||
    Boolean(createdBridgeId && !bridgeDetail);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6">
          {/* Bridge Type Selection */}
          <div className="space-y-3">
            <FormLabel>Connection Type</FormLabel>
            {isEditMode && (
              <p className="text-xs text-muted-foreground">
                Connection type cannot be changed after creation
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              {bridgeTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = selectedType === type.id;
                // In edit mode, disable all types (can't change); in create mode, check availability
                const isDisabled = isEditMode || !type.available;

                return (
                  <button
                    key={type.id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => !isDisabled && setSelectedType(type.id)}
                    className={cn(
                      'relative flex flex-col rounded-lg border p-4 text-left transition-all',
                      isSelected && (type.available || isEditMode)
                        ? 'border-lime bg-lime/10'
                        : 'border-[#2E3835] hover:border-gray-600',
                      isDisabled && !isSelected && 'cursor-not-allowed opacity-60',
                      isEditMode && !isSelected && 'opacity-40'
                    )}
                  >
                    {!isEditMode && !type.available && type.disabledReason && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-[10px] font-semibold text-yellow-500 border border-yellow-500/30">
                          <Lock className="h-2.5 w-2.5" />
                          {type.disabledReason}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Icon
                        className={cn('h-5 w-5', isSelected ? 'text-lime' : 'text-muted-foreground')}
                      />
                      <span
                        className={cn('font-semibold', isSelected ? 'text-lime' : 'text-white')}
                      >
                        {type.label}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bridge Name */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Bridge Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Auckland-Warehouse-01" {...field} />
                </FormControl>
                <FormDescription>
                  A descriptive name to identify this bridge
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Initial Route CIDR - only shown in create mode */}
          {!isEditMode && (
            <FormField
              control={form.control}
              name="initialRouteCidr"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Network Range (CIDR){' '}
                    <span className="text-muted-foreground font-normal">(Optional)</span>
                  </FormLabel>
                  <FormControl>
                    <CidrInput
                      value={field.value || ''}
                      onChange={field.onChange}
                      showValidation={true}
                      placeholder="e.g., 192.168.1.0/24"
                    />
                  </FormControl>
                  <FormDescription>
                    The local network subnet accessible through this bridge
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#2E3835] bg-card2/50">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Network className="h-4 w-4 mr-2" />
                {isEditMode ? 'Update & Continue' : 'Create Bridge'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
